import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { performGlobalSearch, setSearchQuery } from '../../RTK/SearchBare/Searchbar';
import debounce from 'lodash/debounce';
import { Search, X, Loader2, ShoppingBag, User, FileText, CreditCard, CheckCircle, Clock, XCircle } from 'lucide-react';
import OrderDetailsPopup from '../../main-page/order/order-popups/OrderDetailsPopup';
import { updateOrderStatus } from '../../RTK/orders/GetAllOrders';

const GlobalSearch = ({ onClose }) => {
  const dispatch = useDispatch();
  const { searchResults, loading, error, totalResults, searchQuery } = useSelector((state) => state.search);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { currentOrder } = useSelector((state) => state.orders);

  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query.trim()) {
        dispatch(performGlobalSearch(query));
      }
    }, 300),
    [dispatch]
  );

  const handleUpdateStatus = (order_id, status) => {
    dispatch(updateOrderStatus({ order_id, status }));
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    dispatch(setSearchQuery(query));
    debouncedSearch(query);
  };

  const handleItemClick = (item, type) => {
    if (type === 'orders' && item.match_in === 'order') {
      // Format the order data as expected by OrderDetailsPopup
      const formattedOrder = [{
        order_id: item.id,
        order_data: {
          ...item,
          cheapest_agent_name: item.cheapest_agent_name || 'N/A',
          profit_margin: item.profit_margin || 0,
          order_agent_id: item.order_agent_id || 'N/A'
        },
        user_data: {
          username: item.user?.username || 'N/A'
        }
      }];
      setSelectedOrder(formattedOrder);
    }
  };

  const StatusBadge = ({ status, isProcessing = false, order = null }) => {
    const styles = {
      completed: {
        bg: "bg-green-500/10",
        text: "text-green-500",
        icon: CheckCircle,
        label: "مكتمل",
      },
      pending: {
        bg: "bg-yellow-500/10",
        text: "text-yellow-500",
        icon: Clock,
        label: "قيد الانتظار",
      },
      cancelled: {
        bg: "bg-red-500/10",
        text: "text-red-500",
        icon: XCircle,
        label: "ملغي",
      },
      processing: {
        bg: "bg-blue-500/10",
        text: "text-blue-500",
        icon: Loader2,
        label: "قيد المعالجة",
      },
    };

    const getStyle = (status, isProcessing, order) => {
      if (
        status === "pending" &&
        isProcessing &&
        order &&
        order.cheapest_agent_name === "Daily Card"
      ) {
        return styles.processing;
      }
      return styles[status] || styles.pending;
    };

    const style = getStyle(status, isProcessing, order);
    const Icon = style.icon;

    const showSpinningAnimation =
      status === "pending" &&
      isProcessing &&
      order?.cheapest_agent_name === "Daily Card";

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}
      >
        <Icon
          className={`w-3.5 h-3.5 ${
            showSpinningAnimation ? "animate-spin" : ""
          }`}
        />
        {style.label}
      </span>
    );
  };

  const ResultSection = ({ title, items, icon: Icon, type }) => {
    if (!items?.length) return null;

    return (
      <div className="py-2">
        <h3 className="text-sm font-semibold text-gray-400 px-3 mb-1">{title}</h3>
        <div className="space-y-1">
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => handleItemClick(item, type)}
              className="w-full px-3 py-2 text-left hover:bg-gray-800 flex items-center gap-3 text-gray-300 hover:text-white transition-colors"
            >
              <Icon className="w-4 h-4 text-gray-400" />
              <div className="flex-1">
                <span>{item.transaction_id || item.name || item.user?.username}</span>
                {type === 'orders' && (
                  <span className="text-sm text-gray-500 ml-2">
                    {item.product_name}
                  </span>
                )}
              </div>
              {type === 'orders' && (
                <div className="ml-auto">
                  <StatusBadge 
                    status={item.status}
                    isProcessing={item.is_processing}
                    order={item}
                  />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      <div className="fixed top-4 left-4 right-4 max-w-2xl mx-auto bg-gray-900 rounded-xl shadow-2xl border border-gray-800">
        <div className="relative">
          <div className="flex items-center px-4 py-3 border-b border-gray-800">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search everything..."
              className="w-full bg-transparent border-0 focus:ring-0 text-white px-3 py-1 placeholder-gray-500"
              autoFocus
            />
            {loading && <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />}
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {error ? (
            <div className="p-4 text-red-400 text-center">{error}</div>
          ) : searchQuery && !loading ? (
            <div className="max-h-[60vh] overflow-y-auto">
              <ResultSection title="Products" items={searchResults.products} icon={ShoppingBag} type="products" />
              <ResultSection title="Orders" items={searchResults.orders} icon={FileText} type="orders" />
              <ResultSection title="Transactions" items={searchResults.transactions} icon={CreditCard} type="transactions" />
              <ResultSection title="Profiles" items={searchResults.profiles} icon={User} type="profiles" />
              
              {totalResults === 0 && searchQuery && (
                <div className="px-4 py-8 text-center text-gray-400">
                  No results found for "{searchQuery}"
                </div>
              )}
            </div>
          ) : null}
        </div>

        {selectedOrder && (
          <OrderDetailsPopup
            order={selectedOrder}
            StatusBadge={StatusBadge}
            onUpdateStatus={handleUpdateStatus}
            onClose={() => setSelectedOrder(null)}
          />
        )}
      </div>
    </div>
  );
};

export default GlobalSearch;